/**
 * Schema-Driven Report Builder Wizard
 * Fetches available data sources, metrics, dimensions, and join paths from the backend
 * registry to allow full visibility into the data model. Supports multi-source joins,
 * multi-metric selection, point-in-time vs trend reports, and data format badges.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  ChevronLeft, ChevronRight, Sparkles, Calendar, BarChart3,
  Filter, Save, Play, Check, Search, HelpCircle, Clock,
  TrendingUp, Users, MessageSquare, FileText, CheckSquare,
  Database, Link2, Hash, Percent, Timer, Layers
} from 'lucide-react';
import { format } from 'date-fns';

import { useAppTranslation } from '@/contexts/TranslationContext';

import { config as productConfig } from '@/src/config';
const API_URL = productConfig.weaveinApi.baseUrl ?? 'http://localhost:8000';

interface ReportConfig {
  name: string;
  description: string;
  dataSource: string;
  joinedSources: string[];
  dateRange: { startDate: Date; endDate: Date };
  datePreset?: string;
  granularity?: string;
  filters: Record<string, any>;
  metrics: string[];
  dimensions: string[];
  visualizationType: string;
  exportFormat?: string;
}

interface SchemaMetric {
  key: string;
  label: string;
  type: string; // count, percentage, average, sum, duration
  description?: string;
}

interface SchemaDimension {
  key: string;
  label: string;
  description?: string;
}

interface SchemaJoinSource {
  key: string;
  label: string;
  description: string;
  extra_metrics: SchemaMetric[];
  extra_dimensions: SchemaDimension[];
}

interface SchemaSource {
  key: string;
  label: string;
  description: string;
  category: string;
  supports_trends: boolean;
  metrics: SchemaMetric[];
  dimensions: SchemaDimension[];
  joinable_sources: SchemaJoinSource[];
}

interface ReportBuilderWizardProps {
  onRunReport: (config: ReportConfig) => void;
  onSaveReport?: (config: ReportConfig) => void;
}

const METRIC_TYPE_BADGES: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  count: { label: 'Count', color: 'bg-blue-100 text-blue-700', icon: Hash },
  percentage: { label: '%', color: 'bg-green-100 text-green-700', icon: Percent },
  average: { label: 'Avg', color: 'bg-purple-100 text-purple-700', icon: TrendingUp },
  sum: { label: 'Sum', color: 'bg-amber-100 text-amber-700', icon: Layers },
  duration: { label: 'Duration', color: 'bg-orange-100 text-orange-700', icon: Timer },
};

const CATEGORY_COLORS: Record<string, string> = {
  operations: 'border-teal-200 bg-teal-50',
  people: 'border-blue-200 bg-blue-50',
  financial: 'border-amber-200 bg-amber-50',
  clinical: 'border-purple-200 bg-purple-50',
  compliance: 'border-red-200 bg-red-50',
  general: 'border-gray-200 bg-gray-50',
};

export const ReportBuilderWizard: React.FC<ReportBuilderWizardProps> = ({
  onRunReport,
  onSaveReport
}) => {
  const { t } = useAppTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [schemaSources, setSchemaSources] = useState<SchemaSource[]>([]);
  const [schemaLoading, setSchemaLoading] = useState(true);

  const [config, setConfig] = useState<ReportConfig>({
    name: '',
    description: '',
    dataSource: '',
    joinedSources: [],
    dateRange: {
      startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)),
      endDate: new Date()
    },
    datePreset: 'last_30_days',
    granularity: undefined,
    filters: {},
    metrics: [],
    dimensions: [],
    visualizationType: 'table'
  });

  const [aiQuery, setAiQuery] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState<any>(null);

  // Fetch schema on mount
  useEffect(() => {
    const fetchSchema = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const cboId = localStorage.getItem('active_cbo_id');
        const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
        if (cboId) headers['X-CBO-ID'] = cboId;
        const res = await axios.get(`${API_URL}/api/analytics/schema/sources`, {
          headers
        });
        setSchemaSources(res.data.sources || []);
      } catch (e) {
        console.error('Failed to load schema sources:', e);
      } finally {
        setSchemaLoading(false);
      }
    };
    fetchSchema();
  }, []);

  const steps = [
    { id: 'ai', title: 'AI Assistant', icon: Sparkles },
    { id: 'basics', title: 'Report Basics', icon: FileText },
    { id: 'data', title: 'Data Sources', icon: Database },
    { id: 'date', title: 'Date & Type', icon: Calendar },
    { id: 'metrics', title: 'Data Elements', icon: BarChart3 },
    { id: 'review', title: 'Review & Run', icon: Check }
  ];

  // Current selected source object
  const currentSource = schemaSources.find(s => s.key === config.dataSource);

  // Combine all available metrics (primary + joined)
  const allAvailableMetrics = (): SchemaMetric[] => {
    if (!currentSource) return [];
    const metrics = [...currentSource.metrics];
    for (const jk of config.joinedSources) {
      const join = currentSource.joinable_sources.find(j => j.key === jk);
      if (join) metrics.push(...join.extra_metrics);
    }
    return metrics;
  };

  // Combine all available dimensions (primary + joined)
  const allAvailableDimensions = (): SchemaDimension[] => {
    if (!currentSource) return [];
    const dims = [...currentSource.dimensions];
    for (const jk of config.joinedSources) {
      const join = currentSource.joinable_sources.find(j => j.key === jk);
      if (join) dims.push(...join.extra_dimensions);
    }
    return dims;
  };

  // Date presets
  const datePresets = [
    { value: 'today', label: 'Today', getDates: () => ({ start: new Date(), end: new Date() }) },
    { value: 'last_7_days', label: 'Last 7 Days', getDates: () => ({ start: new Date(Date.now() - 7*86400000), end: new Date() }) },
    { value: 'last_30_days', label: 'Last 30 Days', getDates: () => ({ start: new Date(Date.now() - 30*86400000), end: new Date() }) },
    { value: 'last_90_days', label: 'Last 90 Days', getDates: () => ({ start: new Date(Date.now() - 90*86400000), end: new Date() }) },
    { value: 'this_month', label: 'This Month', getDates: () => ({ start: new Date(new Date().getFullYear(), new Date().getMonth(), 1), end: new Date() }) },
    { value: 'last_month', label: 'Last Month', getDates: () => { const d = new Date(); return { start: new Date(d.getFullYear(), d.getMonth()-1, 1), end: new Date(d.getFullYear(), d.getMonth(), 0) }; } },
    { value: 'ytd', label: 'Year to Date', getDates: () => ({ start: new Date(new Date().getFullYear(), 0, 1), end: new Date() }) },
    { value: 'custom', label: 'Custom Range', getDates: () => ({ start: config.dateRange.startDate, end: config.dateRange.endDate }) },
  ];

  const visualizationTypes = [
    { value: 'table', label: 'Data Table', description: 'Detailed tabular view' },
    { value: 'line', label: 'Line Chart', description: 'Trends over time' },
    { value: 'bar', label: 'Bar Chart', description: 'Compare values' },
    { value: 'pie', label: 'Pie Chart', description: 'Proportions' },
    { value: 'area', label: 'Area Chart', description: 'Cumulative trends' },
  ];

  const exportFormats = [
    { value: 'csv', label: 'CSV' },
    { value: 'excel', label: 'Excel' },
    { value: 'pdf', label: 'PDF' },
    { value: 'json', label: 'JSON' },
  ];

  // AI query handler - calls backend AI parser
  const handleAiQuery = async () => {
    if (!aiQuery.trim()) return;
    setAiLoading(true);
    setAiResult(null);
    try {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');
      const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
      if (cboId) headers['X-CBO-ID'] = cboId;
      const res = await axios.post(`${API_URL}/api/analytics/reports/ai-parse`, {
        query: aiQuery,
        current_config: config.dataSource ? config : null,
      }, { headers });

      if (res.data.success && res.data.config) {
        const aiConfig = res.data.config;
        setAiResult(aiConfig);
        // Apply AI suggestions to config
        setConfig(prev => ({
          ...prev,
          dataSource: aiConfig.primary_source || prev.dataSource,
          joinedSources: aiConfig.joined_sources || prev.joinedSources,
          metrics: aiConfig.metrics || prev.metrics,
          dimensions: aiConfig.dimensions || prev.dimensions,
          granularity: aiConfig.granularity || prev.granularity,
          visualizationType: aiConfig.visualization_type || prev.visualizationType,
          name: aiConfig.report_name || prev.name,
          description: aiConfig.report_description || prev.description,
        }));
      }
    } catch (e) {
      console.error('AI parse error:', e);
    } finally {
      setAiLoading(false);
    }
  };

  const handleDatePresetChange = (preset: string) => {
    const p = datePresets.find(d => d.value === preset);
    if (p) {
      const dates = p.getDates();
      setConfig({ ...config, datePreset: preset, dateRange: { startDate: dates.start, endDate: dates.end } });
    }
  };

  const toggleMetric = (key: string) => {
    setConfig(prev => ({
      ...prev,
      metrics: prev.metrics.includes(key) ? prev.metrics.filter(m => m !== key) : [...prev.metrics, key]
    }));
  };

  const toggleDimension = (key: string) => {
    setConfig(prev => ({
      ...prev,
      dimensions: prev.dimensions.includes(key) ? prev.dimensions.filter(d => d !== key) : [...prev.dimensions, key]
    }));
  };

  const toggleJoin = (key: string) => {
    setConfig(prev => {
      const joined = prev.joinedSources.includes(key)
        ? prev.joinedSources.filter(j => j !== key)
        : [...prev.joinedSources, key];
      // Remove metrics/dims from deselected joins
      if (!joined.includes(key)) {
        const removedJoin = currentSource?.joinable_sources.find(j => j.key === key);
        if (removedJoin) {
          const removeKeys = [...removedJoin.extra_metrics.map(m => m.key), ...removedJoin.extra_dimensions.map(d => d.key)];
          return {
            ...prev,
            joinedSources: joined,
            metrics: prev.metrics.filter(m => !removeKeys.includes(m)),
            dimensions: prev.dimensions.filter(d => !removeKeys.includes(d)),
          };
        }
      }
      return { ...prev, joinedSources: joined };
    });
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: return true;
      case 1: return config.name.length > 0;
      case 2: return config.dataSource.length > 0;
      case 3: return config.dateRange.startDate && config.dateRange.endDate;
      case 4: return config.metrics.length > 0;
      default: return true;
    }
  };

  const selectAllMetrics = () => {
    setConfig(prev => ({ ...prev, metrics: allAvailableMetrics().map(m => m.key) }));
  };

  const clearAllMetrics = () => {
    setConfig(prev => ({ ...prev, metrics: [] }));
  };

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 md:p-6">
        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-8">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;
            return (
              <React.Fragment key={step.id}>
                <button onClick={() => isCompleted ? setCurrentStep(index) : undefined} className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                    isActive ? 'bg-teal-600 text-white shadow-md scale-110' : isCompleted ? 'bg-green-500 text-white cursor-pointer' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {isCompleted ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </div>
                  <span className={`text-xs mt-2 font-medium hidden md:block ${
                    isActive ? 'text-teal-600' : isCompleted ? 'text-green-600' : 'text-gray-500'
                  }`}>{step.title}</span>
                </button>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-1 mx-2 rounded ${isCompleted ? 'bg-green-500' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        <div className="min-h-[400px]">

          {/* ============ STEP 0: AI ASSISTANT ============ */}
          {currentStep === 0 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full mb-3">
                  <Sparkles className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">AI Report Assistant</h2>
                <p className="text-sm text-gray-600 dark:text-gray-300">Describe what you'd like to analyze in plain English</p>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-700 dark:to-gray-600 rounded-xl p-5 border border-purple-100 dark:border-gray-600">
                <textarea
                  value={aiQuery}
                  onChange={(e) => setAiQuery(e.target.value)}
                  placeholder="E.g., 'Show me referral success rates by urgency with SDOH risk scores for the last quarter'"
                  className="w-full px-4 py-3 border border-purple-200 dark:border-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none bg-white dark:bg-gray-800 dark:text-white"
                  rows={3}
                />
                <button
                  onClick={handleAiQuery}
                  disabled={aiLoading || !aiQuery.trim()}
                  className="mt-3 w-full md:w-auto px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-md disabled:opacity-50"
                >
                  {aiLoading ? 'Analyzing...' : 'Get AI Suggestions'}
                </button>
              </div>

              {aiResult && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-green-900 dark:text-green-100 mb-2 flex items-center gap-2">
                    <Check className="w-4 h-4" /> AI Configuration Applied
                  </h3>
                  {aiResult.explanation && <p className="text-sm text-green-800 dark:text-green-200 mb-2">{aiResult.explanation}</p>}
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="px-2 py-1 bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-200 rounded-full">Source: {aiResult.primary_source}</span>
                    {aiResult.metrics?.map((m: string) => (
                      <span key={m} className="px-2 py-1 bg-teal-100 dark:bg-teal-800 text-teal-700 dark:text-teal-200 rounded-full">{m.replace(/_/g, ' ')}</span>
                    ))}
                    {aiResult.granularity && <span className="px-2 py-1 bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-200 rounded-full">Trend: {aiResult.granularity}</span>}
                  </div>
                </div>
              )}

              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Example queries:</h3>
                <div className="space-y-2">
                  {[
                    "Show me referral success rates by urgency with SDOH risk scores for the last quarter",
                    "Monthly trend of member engagement and churn risk",
                    "Compare communication read rates by channel type this month",
                    "Claims denial rates by reason code with recovery amounts",
                    "Referral cycle time and satisfaction by provider match score",
                  ].map((q, i) => (
                    <button key={i} onClick={() => { setAiQuery(q); }} className="w-full text-left px-4 py-2.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:border-purple-300 hover:bg-purple-50 dark:hover:bg-gray-600 transition-all text-sm text-gray-700 dark:text-gray-200">
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ============ STEP 1: REPORT BASICS ============ */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Report Basics</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Report Name <span className="text-red-500">*</span></label>
                  <input type="text" value={config.name} onChange={(e) => setConfig({ ...config, name: e.target.value })}
                    placeholder="E.g., Monthly Referral Performance" className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:bg-gray-700 dark:text-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description (Optional)</label>
                  <textarea value={config.description} onChange={(e) => setConfig({ ...config, description: e.target.value })}
                    placeholder="Brief description..." className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 resize-none dark:bg-gray-700 dark:text-white" rows={3} />
                </div>
              </div>
            </div>
          )}

          {/* ============ STEP 2: DATA SOURCES (Multi-select) ============ */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">Select Data Sources</h2>
                <p className="text-sm text-gray-600 dark:text-gray-300">Choose a primary source, then optionally include related data</p>
              </div>

              {schemaLoading ? (
                <div className="text-center py-12 text-gray-500">Loading data sources...</div>
              ) : (
                <>
                  {/* Primary Source */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Primary Data Source <span className="text-red-500">*</span></label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {schemaSources.map((source) => {
                        const isSelected = config.dataSource === source.key;
                        const catColor = CATEGORY_COLORS[source.category] || CATEGORY_COLORS.general;
                        return (
                          <button key={source.key}
                            onClick={() => setConfig({ ...config, dataSource: source.key, joinedSources: [], metrics: [], dimensions: [] })}
                            className={`p-4 rounded-xl border-2 text-left transition-all ${isSelected ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20 shadow-md' : `${catColor} hover:border-teal-300`}`}>
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <Database className={`w-4 h-4 ${isSelected ? 'text-teal-600' : 'text-gray-500'}`} />
                                  <h3 className="font-semibold text-gray-900 dark:text-white">{source.label}</h3>
                                </div>
                                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">{source.description}</p>
                                <div className="flex flex-wrap gap-1">
                                  <span className="text-xs px-1.5 py-0.5 bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded">{source.metrics.length} metrics</span>
                                  <span className="text-xs px-1.5 py-0.5 bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded">{source.dimensions.length} dimensions</span>
                                  {source.joinable_sources.length > 0 && (
                                    <span className="text-xs px-1.5 py-0.5 bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-300 rounded">{source.joinable_sources.length} joins</span>
                                  )}
                                  {source.supports_trends && <span className="text-xs px-1.5 py-0.5 bg-green-100 dark:bg-green-800 text-green-600 dark:text-green-300 rounded">Trends</span>}
                                </div>
                              </div>
                              {isSelected && <Check className="w-5 h-5 text-teal-600 flex-shrink-0" />}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Related Data (Joins) */}
                  {currentSource && currentSource.joinable_sources.length > 0 && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                        <Link2 className="w-4 h-4" /> Include Related Data <span className="text-gray-400 text-xs font-normal">(Optional — adds metrics & dimensions)</span>
                      </label>
                      <div className="space-y-2">
                        {currentSource.joinable_sources.map((join) => {
                          const isJoined = config.joinedSources.includes(join.key);
                          return (
                            <button key={join.key} onClick={() => toggleJoin(join.key)}
                              className={`w-full p-3 rounded-lg border-2 text-left transition-all ${isJoined ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-blue-300'}`}>
                              <div className="flex items-center justify-between">
                                <div>
                                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{join.label}</h4>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">{join.description}</p>
                                  <div className="flex gap-1 mt-1">
                                    {join.extra_metrics.length > 0 && <span className="text-xs px-1.5 py-0.5 bg-teal-100 text-teal-700 rounded">+{join.extra_metrics.length} metrics</span>}
                                    {join.extra_dimensions.length > 0 && <span className="text-xs px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded">+{join.extra_dimensions.length} dimensions</span>}
                                  </div>
                                </div>
                                {isJoined && <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ============ STEP 3: DATE RANGE & REPORT TYPE ============ */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Date Range & Report Type</h2>

              {/* Report Type Toggle */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Report Type</label>
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => setConfig({ ...config, granularity: undefined })}
                    className={`p-4 rounded-lg border-2 text-left ${!config.granularity ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20' : 'border-gray-200 dark:border-gray-600 hover:border-teal-300'}`}>
                    <h4 className="font-semibold text-gray-900 dark:text-white text-sm">Snapshot (Point-in-Time)</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Aggregated totals for the selected period</p>
                  </button>
                  <button onClick={() => setConfig({ ...config, granularity: 'month' })}
                    className={`p-4 rounded-lg border-2 text-left ${config.granularity ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20' : 'border-gray-200 dark:border-gray-600 hover:border-teal-300'}`}>
                    <h4 className="font-semibold text-gray-900 dark:text-white text-sm">Trend (Time Series)</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Data grouped by time period for trend analysis</p>
                  </button>
                </div>
              </div>

              {/* Granularity */}
              {config.granularity && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Granularity</label>
                  <div className="flex gap-2">
                    {['day', 'week', 'month', 'quarter'].map(g => (
                      <button key={g} onClick={() => setConfig({ ...config, granularity: g })}
                        className={`px-4 py-2 rounded-lg border text-sm font-medium capitalize ${config.granularity === g ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-gray-200 text-gray-700 hover:border-teal-300'}`}>
                        {g}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Date Presets */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Date Range</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {datePresets.map(p => (
                    <button key={p.value} onClick={() => handleDatePresetChange(p.value)}
                      className={`px-3 py-2 rounded-lg border text-sm ${config.datePreset === p.value ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-gray-200 text-gray-700 hover:border-teal-300'}`}>
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {config.datePreset === 'custom' && (
                <div className="grid grid-cols-2 gap-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Start</label>
                    <input type="date" value={format(config.dateRange.startDate, 'yyyy-MM-dd')}
                      onChange={(e) => setConfig({ ...config, dateRange: { ...config.dateRange, startDate: new Date(e.target.value) } })}
                      className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:text-white dark:border-gray-600" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">End</label>
                    <input type="date" value={format(config.dateRange.endDate, 'yyyy-MM-dd')}
                      onChange={(e) => setConfig({ ...config, dateRange: { ...config.dateRange, endDate: new Date(e.target.value) } })}
                      className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:text-white dark:border-gray-600" />
                  </div>
                </div>
              )}

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-blue-800 dark:text-blue-200">
                  {format(config.dateRange.startDate, 'MMM dd, yyyy')} — {format(config.dateRange.endDate, 'MMM dd, yyyy')}
                  {config.granularity && <span className="ml-2 text-blue-600 font-medium">({config.granularity}ly trend)</span>}
                </span>
              </div>
            </div>
          )}

          {/* ============ STEP 4: DATA ELEMENTS (Metrics + Dimensions) ============ */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">Select Data Elements</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Choose metrics to measure and dimensions to group by</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={selectAllMetrics} className="text-xs px-3 py-1 bg-teal-100 text-teal-700 rounded-full hover:bg-teal-200">Select All</button>
                  <button onClick={clearAllMetrics} className="text-xs px-3 py-1 bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200">Clear</button>
                </div>
              </div>

              {/* Metrics */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Metrics <span className="text-red-500">*</span>
                  <span className="text-gray-400 font-normal ml-2">({config.metrics.length} selected)</span>
                </label>

                {/* Group by source */}
                <div className="space-y-4">
                  {/* Primary source metrics */}
                  {currentSource && (
                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">{currentSource.label}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {currentSource.metrics.map(metric => {
                          const badge = METRIC_TYPE_BADGES[metric.type] || METRIC_TYPE_BADGES.count;
                          const BadgeIcon = badge.icon;
                          const isSelected = config.metrics.includes(metric.key);
                          return (
                            <button key={metric.key} onClick={() => toggleMetric(metric.key)}
                              className={`px-3 py-2.5 rounded-lg border-2 text-left transition-all ${isSelected ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20' : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-teal-300'}`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded ${badge.color}`}>
                                    <BadgeIcon className="w-3 h-3" />{badge.label}
                                  </span>
                                  <span className="text-sm font-medium text-gray-900 dark:text-white">{metric.label}</span>
                                </div>
                                {isSelected && <Check className="w-4 h-4 text-teal-600" />}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Joined source metrics */}
                  {config.joinedSources.map(jk => {
                    const join = currentSource?.joinable_sources.find(j => j.key === jk);
                    if (!join || join.extra_metrics.length === 0) return null;
                    return (
                      <div key={jk}>
                        <h4 className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-2 flex items-center gap-1">
                          <Link2 className="w-3 h-3" /> {join.label}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {join.extra_metrics.map(metric => {
                            const badge = METRIC_TYPE_BADGES[metric.type] || METRIC_TYPE_BADGES.count;
                            const BadgeIcon = badge.icon;
                            const isSelected = config.metrics.includes(metric.key);
                            return (
                              <button key={metric.key} onClick={() => toggleMetric(metric.key)}
                                className={`px-3 py-2.5 rounded-lg border-2 text-left transition-all ${isSelected ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20' : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-teal-300'}`}>
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <span className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded ${badge.color}`}>
                                      <BadgeIcon className="w-3 h-3" />{badge.label}
                                    </span>
                                    <span className="text-sm font-medium text-gray-900 dark:text-white">{metric.label}</span>
                                  </div>
                                  {isSelected && <Check className="w-4 h-4 text-teal-600" />}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Dimensions */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Dimensions (Group By)
                  <span className="text-gray-400 font-normal ml-2">(Optional — {config.dimensions.length} selected)</span>
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {allAvailableDimensions().map(dim => {
                    const isSelected = config.dimensions.includes(dim.key);
                    return (
                      <button key={dim.key} onClick={() => toggleDimension(dim.key)}
                        className={`px-3 py-2 rounded-lg border-2 text-left transition-all ${isSelected ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20' : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-indigo-300'}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">{dim.label}</span>
                          {isSelected && <Check className="w-4 h-4 text-indigo-600" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Visualization & Export */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Visualization</label>
                  <div className="grid grid-cols-3 gap-2">
                    {visualizationTypes.map(v => (
                      <button key={v.value} onClick={() => setConfig({ ...config, visualizationType: v.value })}
                        className={`p-2 rounded-lg border text-center text-xs font-medium ${config.visualizationType === v.value ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 text-gray-700 hover:border-purple-300'}`}>
                        {v.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Export Format <span className="text-gray-400 font-normal">(Optional)</span></label>
                  <div className="grid grid-cols-4 gap-2">
                    {exportFormats.map(f => (
                      <button key={f.value} onClick={() => setConfig({ ...config, exportFormat: config.exportFormat === f.value ? undefined : f.value })}
                        className={`p-2 rounded-lg border text-center text-xs font-medium ${config.exportFormat === f.value ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-200 text-gray-700 hover:border-orange-300'}`}>
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ============ STEP 5: REVIEW & RUN ============ */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Review & Run</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-2">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Report</h3>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{config.name}</p>
                  {config.description && <p className="text-xs text-gray-500 dark:text-gray-400">{config.description}</p>}
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-2">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Data Source</h3>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{currentSource?.label}</p>
                  {config.joinedSources.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {config.joinedSources.map(jk => {
                        const j = currentSource?.joinable_sources.find(x => x.key === jk);
                        return j ? <span key={jk} className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">+ {j.label}</span> : null;
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-2">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Date & Type</h3>
                <p className="text-sm text-gray-900 dark:text-white">
                  {format(config.dateRange.startDate, 'MMM dd, yyyy')} — {format(config.dateRange.endDate, 'MMM dd, yyyy')}
                  {config.granularity && <span className="ml-2 text-teal-600 font-medium">({config.granularity}ly trend)</span>}
                  {!config.granularity && <span className="ml-2 text-gray-500">(snapshot)</span>}
                </p>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-3">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Metrics ({config.metrics.length})</h3>
                <div className="flex flex-wrap gap-2">
                  {config.metrics.map(mk => {
                    const m = allAvailableMetrics().find(x => x.key === mk);
                    const badge = m ? METRIC_TYPE_BADGES[m.type] || METRIC_TYPE_BADGES.count : METRIC_TYPE_BADGES.count;
                    return (
                      <span key={mk} className="inline-flex items-center gap-1 px-2.5 py-1 bg-teal-100 text-teal-700 text-xs font-medium rounded-full">
                        <span className={`text-xs px-1 py-0.5 rounded ${badge.color}`}>{badge.label}</span>
                        {m?.label || mk.replace(/_/g, ' ')}
                      </span>
                    );
                  })}
                </div>
                {config.dimensions.length > 0 && (
                  <>
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mt-2">Dimensions ({config.dimensions.length})</h3>
                    <div className="flex flex-wrap gap-2">
                      {config.dimensions.map(dk => {
                        const d = allAvailableDimensions().find(x => x.key === dk);
                        return <span key={dk} className="px-2.5 py-1 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full">{d?.label || dk.replace(/_/g, ' ')}</span>;
                      })}
                    </div>
                  </>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <button onClick={() => onRunReport(config)}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700 text-white rounded-lg shadow-md flex items-center justify-center gap-2">
                  <Play className="w-5 h-5" /> Run Report
                </button>
                {onSaveReport && (
                  <button onClick={() => onSaveReport(config)}
                    className="px-6 py-3 bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-white rounded-lg hover:bg-gray-50 flex items-center gap-2">
                    <Save className="w-5 h-5" /> Save
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
          <button onClick={() => setCurrentStep(Math.max(0, currentStep - 1))} disabled={currentStep === 0}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 ${currentStep === 0 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
            <ChevronLeft className="w-4 h-4" /> Previous
          </button>
          {currentStep < steps.length - 1 ? (
            <button onClick={() => setCurrentStep(currentStep + 1)} disabled={!canProceed()}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 ${canProceed() ? 'bg-teal-600 text-white hover:bg-teal-700 shadow-md' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}>
              Next <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <div className="text-sm text-gray-500">Review and run your report</div>
          )}
        </div>
      </div>
    </div>
  );
};
